'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { PAGAMENTO_MAX_ERROS_CONSECUTIVOS, PAGAMENTO_POLLING_BACKOFF_FATOR, PAGAMENTO_POLLING_INTERVALO_MAX_MS, PAGAMENTO_POLLING_INTERVALO_MS, PAGAMENTO_POLLING_TETO_MS } from '@/shared/constants/pagamentoPolling';
import { abrirCheckoutPagamento, type ResultadoCheckout } from '@/shared/utils/abrirCheckoutPagamento';
import { proximoIntervaloComBackoff } from '@/shared/utils/proximoIntervaloComBackoff';

/**
 * As fases da espera por um pagamento de gateway, e todas do NAVEGADOR.
 *
 * `AGUARDANDO` é passiva (a aba do checkout está aberta) e `VERIFICANDO` é a checagem
 * que o usuário pediu. Nenhuma das duas afirma que o pagamento entrou: quem diz isso é
 * o servidor, e a sobreposição some assim que o dado de origem muda.
 */
export type FaseCheckout = 'FECHADO' | 'AGUARDANDO' | 'VERIFICANDO';
export type MotivoDesistencia = 'TEMPO' | 'ERRO';
export interface UseCheckoutPagamentoOpcoes {
  /**
   * Refaz a leitura que revela o desfecho — é o que o polling consulta. A consulta
   * entra por parâmetro para que este hook não conheça feature nenhuma: aqui não pode
   * haver import de `features/`, e a régua de "o que é pagar" não é dele.
   */
  consultar: () => void;
  /**
   * Erro corrente da leitura. Sem teto de ERRO, um servidor fora do ar levaria
   * requisição a cada 5–30 s pelos 20 minutos inteiros do teto de relógio.
   */
  erroLeitura?: string | null;
  /**
   * Enquanto `true`, a tela relê sozinha mesmo sem ninguém clicar em "já paguei" —
   * é o que sustenta a promessa "esta tela atualiza sozinha" enquanto a aba do
   * checkout está aberta ou a cobrança ainda está sendo emitida.
   *
   * Passivo é **releitura**, nunca a rota de verificação: aquela tem rate limit de
   * poucas chamadas por minuto e é ação do usuário, não relógio.
   */
  pollingPassivo?: boolean;
  /** Fim da linha do polling ATIVO: teto de relógio ou de erros seguidos. */
  aoDesistir?: (motivo: MotivoDesistencia) => void;
}
export interface UseCheckoutPagamentoResult {
  fase: FaseCheckout;
  popupBloqueado: boolean;
  /** Abre a aba do gateway e entra em `AGUARDANDO` se ela de fato abriu. */
  abrirCheckout: (url: string | null | undefined) => ResultadoCheckout;
  /** "Já paguei": entra em `VERIFICANDO` e passa a reconsultar com backoff. */
  verificarPagamento: () => void;
  /** Desistir da espera: para o polling e volta a `FECHADO`. */
  fecharCheckout: () => void;
  limparPopupBloqueado: () => void;
}
type ModoPolling = 'ATIVO' | 'PASSIVO';

/**
 * O núcleo de "abrir o gateway e esperar o webhook", compartilhado pelas telas que
 * cobram.
 *
 * Mora em `shared` porque as duas implementações que existiam divergiram no que
 * importava: a do card de custas usava `abrirCheckoutPagamento` e tinha polling; a de
 * autorização de AR testava o retorno de `window.open(..., 'noopener')` — que a spec
 * manda ser `null` — e por isso acusava pop-up bloqueado em TODO pagamento, e nunca
 * chegava a esperar nada. Duas cópias do mesmo comportamento é como um bug conserta
 * de um lado e sobrevive do outro.
 */
export function useCheckoutPagamento(opcoes) {
  const $ = _c(30);
  const [fase, setFase] = useState("FECHADO");
  const [popupBloqueado, setPopupBloqueado] = useState(false);
  const [passivoExpirado, setPassivoExpirado] = useState(false);
  const agendamentoRef = useRef(null);
  const modoRef = useRef(null);
  const consultarRef = useRef(opcoes.consultar);
  const erroLeituraRef = useRef(opcoes.erroLeitura ?? null);
  const aoDesistirRef = useRef(opcoes.aoDesistir);
  let t0;
  let t1;
  if ($[0] !== opcoes.consultar) {
    t0 = () => {
      consultarRef.current = opcoes.consultar;
    };
    t1 = [opcoes.consultar];
    $[0] = opcoes.consultar;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== opcoes.erroLeitura) {
    t2 = () => {
      erroLeituraRef.current = opcoes.erroLeitura ?? null;
    };
    t3 = [opcoes.erroLeitura];
    $[3] = opcoes.erroLeitura;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== opcoes.aoDesistir) {
    t4 = () => {
      aoDesistirRef.current = opcoes.aoDesistir;
    };
    t5 = [opcoes.aoDesistir];
    $[6] = opcoes.aoDesistir;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (agendamentoRef.current) {
        clearTimeout(agendamentoRef.current);
      }
      agendamentoRef.current = null;
      modoRef.current = null;
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const pararPolling = t6;
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => pararPolling;
    t8 = [pararPolling];
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (modo, desistir) => {
      pararPolling();
      modoRef.current = modo;
      const inicio = Date.now();
      let intervalo = PAGAMENTO_POLLING_INTERVALO_MS;
      let errosConsecutivos = 0;
      const consultar = () => {
        if (Date.now() - inicio > PAGAMENTO_POLLING_TETO_MS) {
          pararPolling();
          desistir("TEMPO");
          return;
        }
        errosConsecutivos = erroLeituraRef.current === null ? 0 : errosConsecutivos + 1;
        if (errosConsecutivos >= PAGAMENTO_MAX_ERROS_CONSECUTIVOS) {
          pararPolling();
          desistir("ERRO");
          return;
        }
        consultarRef.current();
        intervalo = proximoIntervaloComBackoff(intervalo, PAGAMENTO_POLLING_BACKOFF_FATOR, PAGAMENTO_POLLING_INTERVALO_MAX_MS);
        agendamentoRef.current = setTimeout(consultar, intervalo);
      };
      agendamentoRef.current = setTimeout(consultar, intervalo);
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const iniciarPolling = t9;
  const pollingPassivo = opcoes.pollingPassivo ?? false;
  let t10;
  let t11;
  if ($[13] !== pollingPassivo) {
    t10 = () => {
      if (!pollingPassivo) {
        setPassivoExpirado(false);
      }
    };
    t11 = [pollingPassivo];
    $[13] = pollingPassivo;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[16] !== fase || $[17] !== passivoExpirado || $[18] !== pollingPassivo) {
    t12 = () => {
      if (!pollingPassivo || passivoExpirado || fase === "VERIFICANDO") {
        return;
      }
      iniciarPolling("PASSIVO", () => setPassivoExpirado(true));
      return () => {
        if (modoRef.current === "PASSIVO") {
          pararPolling();
        }
      };
    };
    t13 = [pollingPassivo, passivoExpirado, fase, iniciarPolling, pararPolling];
    $[16] = fase;
    $[17] = passivoExpirado;
    $[18] = pollingPassivo;
    $[19] = t12;
    $[20] = t13;
  } else {
    t12 = $[19];
    t13 = $[20];
  }
  useEffect(t12, t13);
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = url => {
      const resultado = abrirCheckoutPagamento(url);
      if (resultado === "URL_INVALIDA") {
        return resultado;
      }
      if (resultado === "POPUP_BLOQUEADO") {
        setPopupBloqueado(true);
        return resultado;
      }
      setPopupBloqueado(false);
      setFase("AGUARDANDO");
      return resultado;
    };
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const abrirCheckout = t14;
  let t15;
  if ($[22] !== fase) {
    t15 = () => {
      if (fase === "VERIFICANDO") {
        return;
      }
      setFase("VERIFICANDO");
      iniciarPolling("ATIVO", motivo => {
        setFase("FECHADO");
        aoDesistirRef.current?.(motivo);
      });
    };
    $[22] = fase;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const verificarPagamento = t15;
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      pararPolling();
      setFase("FECHADO");
    };
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const fecharCheckout = t16;
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setPopupBloqueado(false);
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== fase || $[27] !== popupBloqueado || $[28] !== verificarPagamento) {
    t18 = {
      fase,
      popupBloqueado,
      abrirCheckout,
      verificarPagamento,
      fecharCheckout,
      limparPopupBloqueado: t17
    };
    $[26] = fase;
    $[27] = popupBloqueado;
    $[28] = verificarPagamento;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  return t18;
}